import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { searchGamesByName, searchGamesByDescription } from '../services/gemini';
import backdropGif from '../assets/gamesbackdrop.gif';
import './GameSearch.css';

// Smooth loading bar component with refined stages
const LoadingBar = ({ stage, stageProgress, isActive, hasError = false }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [displayLabel, setDisplayLabel] = useState('正在连接大模型...');
  const startTimeRef = useRef(Date.now());
  const lastProgressRef = useRef(0);
  
  useEffect(() => {
    if (!isActive) {
      setDisplayProgress(0);
      setDisplayLabel('正在连接大模型...');
      startTimeRef.current = Date.now();
      lastProgressRef.current = 0;
      return;
    }
    
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      
      let targetProgress;
      let label;
      
      if (stage === 'llm') {
        const variance = Math.sin(elapsed * 0.5) * 0.02;
        
        if (elapsed < 3) {
          const phase1Progress = Math.min(elapsed / 3, 1);
          targetProgress = phase1Progress * 5;
          label = '正在连接大模型...';
        } else if (elapsed < 28) {
          const phase2Elapsed = elapsed - 3;
          const phase2Duration = 25 + (variance * 5);
          const phase2Progress = Math.min(phase2Elapsed / phase2Duration, 1);
          const eased = 1 - (1 - phase2Progress) * (1 - phase2Progress);
          targetProgress = 5 + eased * 40;
          label = '正在获取游戏信息...';
        } else if (elapsed < 45) {
          const phase3Elapsed = elapsed - 28;
          const phase3Duration = 17 + (variance * 3);
          const phase3Progress = Math.min(phase3Elapsed / phase3Duration, 1);
          const eased = 1 - Math.pow(1 - phase3Progress, 1.5);
          targetProgress = 45 + eased * 25;
          label = '正在分析游戏列表...';
        } else {
          targetProgress = 68 + Math.min((elapsed - 45) * 0.1, 1.5);
          label = '正在分析游戏列表...';
        }
      } else if (stage === 'images') {
        targetProgress = 70 + (stageProgress / 100) * 29;
        label = '正在获取游戏图片...';
      } else if (stage === 'done') {
        targetProgress = 99;
        label = '即将完成...';
      } else {
        targetProgress = lastProgressRef.current;
        label = displayLabel;
      }
      
      targetProgress = Math.min(targetProgress, 99);
      
      const minProgress = lastProgressRef.current + 0.1;
      const newProgress = Math.max(targetProgress, minProgress);
      
      const maxIncrement = 2;
      let smoothedProgress = Math.min(
        newProgress,
        lastProgressRef.current + maxIncrement
      );
      
      smoothedProgress = Math.min(smoothedProgress, 99);
      
      if (Math.abs(smoothedProgress - lastProgressRef.current) >= 0.05) {
        lastProgressRef.current = smoothedProgress;
        setDisplayProgress(smoothedProgress);
      }
      
      setDisplayLabel(label);
    }, 100);
    
    return () => clearInterval(interval);
  }, [stage, stageProgress, isActive, displayLabel]);
  
  if (!isActive && !hasError) return null;
  
  if (hasError) {
    return (
      <div className="gs-loading-bar">
        <div className="gs-progress-track gs-progress-error">
          <div className="gs-progress-fill gs-progress-fill-error" style={{ width: '100%' }} />
        </div>
        <div className="gs-progress-info">
          <p className="gs-error-text">搜索出错，请重试</p>
          <p className="gs-error-hint">
            如果在电脑使用，请点击右键-检查，并将"控制台"页截图反馈至"反馈中心"，十分感谢！
          </p>
        </div>
      </div>
    );
  }
  
  const displayPercent = Math.min(Math.floor(displayProgress), 99);
  
  return (
    <div className="gs-loading-bar">
      <div className="gs-progress-track">
        <div className="gs-progress-shimmer" />
        <div 
          className="gs-progress-fill"
          style={{ 
            width: `${Math.min(displayProgress, 99)}%`,
            transition: 'width 150ms ease-out'
          }}
        >
          <div className="gs-progress-glow" />
        </div>
      </div>
      <div className="gs-progress-labels">
        <span className="gs-progress-stage">{displayLabel}</span>
        <span className="gs-progress-percent">{displayPercent}%</span>
      </div>
      <p className="gs-progress-hint">AI 搜索可能需要 1-2 分钟，请耐心等待</p>
    </div>
  );
};

const I18N = {
  title: "找游戏",
  subtitle: "怀旧游戏博物馆",
  rememberName: "我记得游戏名字",
  dontRememberName: "我不记得名字",
  namePlaceholder: "输入游戏名称...",
  platformPlaceholder: "运行平台（可选，如：PS1、NES、DOS等）",
  descriptionPlaceholder: "描述这款游戏的运行平台，特征、玩法、画面等，越详细越可能找到游戏噢...",
  search: "搜索",
  searching: "搜索中...",
  selectGame: "请选择您要申请的游戏:",
  noResults: "未找到匹配的游戏",
  chineseName: "中文名",
  englishName: "英文名",
  developer: "开发商",
  platform: "平台",
  releaseYear: "发行年份",
  description: "简介",
  screenshots: "游戏截图",
  emulatable: "可在网站运行",
  notEmulatable: "此游戏无法在网页运行",
  submit: "提交申请",
  back: "返回",
  success: "申请已提交！",
  successMessage: "感谢您的申请，我们会尽快处理。",
  backToHome: "返回首页"
};

const RECENT_APPLICATION_LIMIT = 100;
const RECENT_APPLICATIONS_PER_PAGE = 10;

const STATUS_MAP = {
  pending: { label: '待处理', color: 'gs-status-pending', bg: 'gs-status-bg-pending' },
  ignored: { label: '暂不处理', color: 'gs-status-ignored', bg: 'gs-status-bg-ignored' },
  added: { label: '已添加', color: 'gs-status-added', bg: 'gs-status-bg-added' },
};

const appToCandidate = (app) => {
  let screenshots = [];
  if (app.screenshots) {
    try {
      const parsed = typeof app.screenshots === 'string' ? JSON.parse(app.screenshots) : app.screenshots;
      if (Array.isArray(parsed)) screenshots = parsed.filter((s) => s && s !== '未找到截图');
    } catch (e) {}
  }
  return {
    id: String(app.id),
    title_cn: app.title_cn || '',
    title_en: app.title_en || '',
    title_jp: app.title_jp || undefined,
    platform: app.platform || '',
    developer: app.developer || '',
    publisher: app.publisher || '',
    release_date: app.release_date || '',
    description: app.description || '',
    poster: app.poster || '',
    screenshots,
    isEmulatable: app.is_emulatable || 'no',
    isInLibrary: !!app.is_in_library,
    libraryGameId: app.library_game_id || undefined,
  };
};

const GameCandidateCard = ({ game, onSelect, isSelected, compact = false }) => {
  const t = I18N;
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [screenshotLoading, setScreenshotLoading] = useState({});
  const [descExpanded, setDescExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    const el = descRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }
  }, [game.description, descExpanded]);
  
  return (
    <div 
      onClick={onSelect}
      className={`gs-card ${isSelected ? 'gs-card-selected' : ''}`}
    >
      <div className="gs-card-header">
        <div className="gs-card-poster">
          {imageError || !game.poster ? (
            <div className="gs-card-no-image">
              <span>暂未找到图片</span>
            </div>
          ) : (
            <div className="gs-card-image-wrapper">
              {imageLoading && (
                <div className="gs-card-image-loading">
                  <div className="gs-spinner"></div>
                </div>
              )}
              <img 
                src={game.poster} 
                alt={game.title_en} 
                className={`gs-card-image ${imageLoading ? 'gs-hidden' : ''}`}
                onLoad={() => setImageLoading(false)}
                onError={() => { setImageError(true); setImageLoading(false); }}
              />
            </div>
          )}
        </div>
        <div className="gs-card-left">
          <div className="gs-card-titles">
            <h3 className="gs-card-title-cn">{game.title_cn}</h3>
            <p className="gs-card-title-en">{game.title_en}</p>
            {game.title_jp && <p className="gs-card-title-jp">{game.title_jp}</p>}
          </div>
          <div className="gs-card-meta">
            <div className="gs-card-meta-item">
              <p className="gs-card-meta-label">{t.developer}</p>
              <p className="gs-card-meta-value">{game.developer || '-'}</p>
            </div>
            <div className="gs-card-meta-item">
              <p className="gs-card-meta-label">{t.platform}</p>
              <p className="gs-card-meta-value">{game.platform}</p>
            </div>
            <div className="gs-card-meta-item">
              <p className="gs-card-meta-label">{t.releaseYear}</p>
              <p className="gs-card-meta-value">{game.release_date}</p>
            </div>
            {!compact && (
            <div className="gs-card-meta-item">
              <p className="gs-card-meta-label">{t.emulatable}</p>
              <p className={`gs-card-meta-value ${game.isInLibrary ? 'gs-text-green gs-text-bold' : game.isEmulatable === 'yes' ? 'gs-text-green' : game.isEmulatable === 'maybe' ? 'gs-text-yellow' : 'gs-text-red'}`}>
                {game.isInLibrary ? '已经收录' : game.isEmulatable === 'yes' ? '✓' : game.isEmulatable === 'maybe' ? '可能可模拟' : '✗'}
              </p>
            </div>
            )}
          </div>
          {game.description && (
            <div className="gs-card-desc">
              <p className="gs-card-meta-label">{t.description}</p>
              <p ref={descRef} className={`gs-card-desc-text ${descExpanded ? 'gs-card-desc-expanded' : ''}`}>{game.description}</p>
              {(isClamped || descExpanded) && (
                <button
                  className="gs-card-desc-toggle"
                  onClick={(e) => { e.stopPropagation(); setDescExpanded(!descExpanded); }}
                >
                  {descExpanded ? '收起' : '展开'}
                </button>
              )}
            </div>
          )}
          {game.screenshots && game.screenshots.length > 0 && (
            <div className="gs-card-screenshots">
              <p className="gs-card-meta-label">{t.screenshots}</p>
              <div className="gs-card-screenshots-row">
                {game.screenshots.slice(0, 3).map((screenshot, idx) => (
                  <div key={idx} className="gs-card-screenshot-item">
                    {screenshotLoading[idx] !== false && (
                      <div className="gs-card-screenshot-loading">
                        <div className="gs-spinner gs-spinner-sm"></div>
                      </div>
                    )}
                    <img 
                      src={screenshot} 
                      alt={`Screenshot ${idx + 1}`}
                      className={`gs-card-screenshot-img ${screenshotLoading[idx] === false ? '' : 'gs-hidden'}`}
                      onLoad={() => setScreenshotLoading(prev => ({...prev, [idx]: false}))}
                      onError={(e) => { e.target.style.display = 'none'; setScreenshotLoading(prev => ({...prev, [idx]: false})); }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="gs-card-body">
          {!compact && game.isInLibrary && (
            <div className="gs-card-library-notice">
              <p>发现匹配游戏</p>
              <a
                href={`/game/${game.libraryGameId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="gs-btn gs-btn-gold gs-btn-sm"
              >
                现在游玩
              </a>
            </div>
          )}
          
          {!compact && !game.isInLibrary && game.isEmulatable === 'no' && (
            <div className="gs-card-not-emulatable">
              <p>{t.notEmulatable}</p>
            </div>
          )}

          {!compact && game.existingApplicationId && (
            <div className="gs-card-existing-request">
              <p>该游戏已有待处理申请</p>
              <a
                href={`/search/track/${game.existingApplicationId}`}
                onClick={(e) => e.stopPropagation()}
                className="gs-btn gs-btn-outline gs-btn-sm"
              >
                查看申请 #{game.existingApplicationId}
              </a>
            </div>
          )}
      </div>
    </div>
  );
};

export default function GameSearch() {
  const [step, setStep] = useState('choice');
  const [searchMode, setSearchMode] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [platformInput, setPlatformInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  
  // Progress tracking state
  const [progressStage, setProgressStage] = useState('llm');
  const [stageProgress, setStageProgress] = useState(0);
  const [searchError, setSearchError] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);
  const [recentApps, setRecentApps] = useState([]);
  const [recentPage, setRecentPage] = useState(1);
  const [recentLoading, setRecentLoading] = useState(false);
  const [trackingApp, setTrackingApp] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState(null);
  const [bgLoaded, setBgLoaded] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://rgm.games/api';
  const t = I18N;
  const { trackId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Read route on mount for shareable tracking URLs
  useEffect(() => {
    if (trackId) {
      const id = parseInt(trackId);
      setStep('tracking');
      setTrackingLoading(true);
      fetch(`${apiUrl}/game-applications/${id}`)
        .then(res => { if (!res.ok) throw new Error('Not found'); return res.json(); })
        .then(data => setTrackingApp(data))
        .catch(() => setTrackingError('未找到该申请'))
        .finally(() => setTrackingLoading(false));
    } else if (location.pathname === '/search/recent') {
      setStep('recent');
      setRecentLoading(true);
      fetch(`${apiUrl}/game-applications/recent?limit=${RECENT_APPLICATION_LIMIT}`)
        .then(res => res.json())
        .then(data => {
          setRecentApps(data);
          setRecentPage(1);
        })
        .catch(() => {})
        .finally(() => setRecentLoading(false));
    }
  }, [apiUrl, trackId, location.pathname]);
  
  const handleModeSelect = (mode) => {
    setSearchMode(mode);
    setStep('input');
  };
  
  const handleSearch = async () => {
    if (!inputValue.trim()) return;
    
    setIsLoading(true);
    setCandidates([]);
    setSelectedGame(null);
    setSearchError(false);
    
    setProgressStage('llm');
    setStageProgress(0);
    
    const handleProgress = (stage, progress) => {
      setProgressStage(stage);
      setStageProgress(progress);
    };
    
    try {
      let results = [];
      
      if (searchMode === 'name') {
        results = await searchGamesByName(inputValue, platformInput.trim(), handleProgress);
      } else if (searchMode === 'description') {
        results = await searchGamesByDescription(inputValue, handleProgress);
      }
      
      try {
        const existingResponse = await fetch(`${apiUrl}/game-applications/check-existing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidates: results })
        });
        if (existingResponse.ok) {
          const { matches = [] } = await existingResponse.json();
          const matchesByIndex = new Map(matches.map((match) => [match.index, match.application_id]));
          results = results.map((game, index) => ({
            ...game,
            existingApplicationId: matchesByIndex.get(index) || null
          }));
        }
      } catch (existingCheckError) {
        console.error('Existing application check failed:', existingCheckError);
      }

      setCandidates(results);
      setStep('results');
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedGame || isSubmitting) return;
    if (selectedGame.isEmulatable === 'no') {
      setSubmitError('该游戏暂不支持在网站运行，无法提交申请。');
      return;
    }
    if (selectedGame.existingApplicationId) {
      setSubmitError(`该游戏已有待处理申请 #${selectedGame.existingApplicationId}。`);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch(`${apiUrl}/game-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title_cn: selectedGame.title_cn,
          title_en: selectedGame.title_en,
          title_jp: selectedGame.title_jp || null,
          platform: selectedGame.platform,
          developer: selectedGame.developer || null,
          publisher: selectedGame.publisher || null,
          release_date: selectedGame.release_date,
          description: selectedGame.description || null,
          poster: selectedGame.poster || null,
          screenshots: selectedGame.screenshots || [],
          is_emulatable: selectedGame.isEmulatable,
          is_in_library: selectedGame.isInLibrary || false,
          library_game_id: selectedGame.libraryGameId || null,
          search_mode: searchMode,
          search_query: inputValue
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409 && errorData.existing_application_id) {
          const existingApplicationId = errorData.existing_application_id;
          setSelectedGame((game) => ({ ...game, existingApplicationId }));
          setCandidates((games) => games.map((game) => (
            game.id === selectedGame.id ? { ...game, existingApplicationId } : game
          )));
        }
        throw new Error(errorData.error || `提交失败 (${response.status})`);
      }
      
      const data = await response.json();
      setSubmittedId(data.id);
      console.log('Game application submitted successfully, id:', data.id);
      setStep('success');
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitError(error.message || '提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReset = () => {
    setStep('choice');
    setSearchMode(null);
    setInputValue('');
    setPlatformInput('');
    setCandidates([]);
    setSelectedGame(null);
    setSubmittedId(null);
    setRecentPage(1);
    navigate('/search', { replace: true });
  };

  const fetchRecentApps = async () => {
    setRecentLoading(true);
    try {
      const res = await fetch(`${apiUrl}/game-applications/recent?limit=${RECENT_APPLICATION_LIMIT}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setRecentApps(data);
      setRecentPage(1);
    } catch (e) {
      console.error('Error fetching recent apps:', e);
    } finally {
      setRecentLoading(false);
    }
  };

  const fetchTrackingApp = async (id) => {
    setTrackingLoading(true);
    setTrackingError(null);
    setTrackingApp(null);
    try {
      const res = await fetch(`${apiUrl}/game-applications/${id}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('未找到该申请');
        throw new Error('查询失败');
      }
      const data = await res.json();
      setTrackingApp(data);
    } catch (e) {
      setTrackingError(e.message || '查询失败');
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleViewRecent = () => {
    setStep('recent');
    setRecentPage(1);
    navigate('/search/recent', { replace: true });
    fetchRecentApps();
  };

  const handleTrack = (id) => {
    setStep('tracking');
    navigate(`/search/track/${id}`, { replace: true });
    fetchTrackingApp(id);
  };

  const recentTotalPages = Math.max(1, Math.ceil(recentApps.length / RECENT_APPLICATIONS_PER_PAGE));
  const paginatedRecentApps = recentApps.slice(
    (recentPage - 1) * RECENT_APPLICATIONS_PER_PAGE,
    recentPage * RECENT_APPLICATIONS_PER_PAGE
  );
  
  return (
    <div className="gs-page">
      {(step === 'choice' || step === 'input') && (
        <div className={`gs-hero-backdrop ${bgLoaded ? 'gs-hero-backdrop--loaded' : ''}`}>
          <img
            src={backdropGif}
            alt=""
            className="gs-hero-backdrop-img"
            onLoad={() => setBgLoaded(true)}
          />
          <div className="gs-hero-backdrop-overlay" />
        </div>
      )}
      <Navbar />
      <div className="gs-container">
        <main className="gs-main">
          <div className="gs-content">
            {step === 'choice' && (
              <div className="gs-fade-in gs-hero">
                <div className="gs-hero-text">
                  <h1 className="gs-hero-headline">记忆中的那款游戏，叫什么来着？</h1>
                  <p className="gs-hero-sub">用 AI 帮你找回童年记忆里的游戏，或直接搜索你想要的游戏</p>
                </div>

                <div className="gs-choice-grid">
                  <button
                    onClick={() => handleModeSelect('description')}
                    className="gs-choice-btn gs-choice-primary"
                  >
                    <div className="gs-choice-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/>
                        <path d="M21 21l-4.35-4.35"/>
                        <path d="M11 8a3 3 0 0 0-3 3"/>
                      </svg>
                    </div>
                    <div className="gs-choice-text">
                      <h3 className="gs-choice-heading">描述记忆中的游戏</h3>
                      <p className="gs-choice-desc">还记得画面、玩法或角色吗？告诉我们，AI 帮你找到它</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => handleModeSelect('name')}
                    className="gs-choice-btn"
                  >
                    <div className="gs-choice-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                      </svg>
                    </div>
                    <div className="gs-choice-text">
                      <h3 className="gs-choice-heading">我知道游戏名字</h3>
                      <p className="gs-choice-desc">直接搜索游戏名称，申请添加到博物馆</p>
                    </div>
                  </button>
                </div>
                
                <div className="gs-recent-link-wrapper">
                  <button
                    onClick={handleViewRecent}
                    className="gs-recent-link"
                  >
                    查看近期游戏申请
                  </button>
                </div>
              </div>
            )}
            
            {step === 'input' && (
              <div className="gs-fade-in gs-input-step">
                <button 
                  onClick={handleReset}
                  className="gs-back-btn"
                >
                  ← {t.back}
                </button>
                
                <div className="gs-input-panel">
                  <h2 className="gs-input-heading">
                    {searchMode === 'name' ? t.rememberName : t.dontRememberName}
                  </h2>
                  
                  {searchMode === 'name' ? (
                    <div className="gs-input-fields">
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder={t.namePlaceholder}
                        className="gs-input"
                        autoFocus
                      />
                      <input
                        type="text"
                        value={platformInput}
                        onChange={(e) => setPlatformInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder={t.platformPlaceholder}
                        className="gs-input"
                      />
                    </div>
                  ) : (
                    <textarea
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={t.descriptionPlaceholder}
                      className="gs-textarea"
                      autoFocus
                    />
                  )}
                  
                  <button
                    onClick={handleSearch}
                    disabled={isLoading || !inputValue.trim()}
                    className="gs-btn gs-btn-gold gs-btn-full"
                    style={{ marginTop: '1.5rem' }}
                  >
                    {isLoading ? t.searching : t.search}
                  </button>
                  
                  <LoadingBar 
                    stage={progressStage}
                    stageProgress={stageProgress}
                    isActive={isLoading}
                    hasError={searchError}
                  />
                </div>
              </div>
            )}
            
            {step === 'results' && (
              <div className="gs-fade-in gs-results-section">
                <button 
                  onClick={() => setStep('input')}
                  className="gs-back-btn"
                >
                  ← {t.back}
                </button>
                      
                <h2 className="gs-results-heading">请选择游戏</h2>
                
                {candidates.length === 0 ? (
                  <div className="gs-no-results">
                    <p>没有找到相关游戏</p>
                  </div>
                ) : (
                  <div className="gs-results-list">
                    {candidates.map((game) => (
                      <GameCandidateCard
                        key={game.id}
                        game={game}
                        isSelected={selectedGame?.id === game.id}
                        onSelect={() => setSelectedGame(game)}
                      />
                    ))}
                  </div>
                )}

                {candidates.length > 0 && (
                  <div className="gs-bottom-bar">
                    <div className="gs-bottom-bar-inner">
                      <div className="gs-bottom-bar-info">
                        {selectedGame ? (
                          selectedGame.isInLibrary ? (
                            <p className="gs-text-green">
                              <span className="gs-text-bold">{selectedGame.title_cn}</span>
                              <span className="gs-text-muted">（{selectedGame.platform}）</span>
                              <span className="gs-ml-2">发现匹配游戏</span>
                            </p>
                          ) : (
                            <p className="gs-text-light">
                              已选择: <span className="gs-text-gold gs-text-bold">{selectedGame.title_cn}</span>
                              <span className="gs-text-muted">（{selectedGame.platform}）</span>
                            </p>
                          )
                        ) : (
                          <p className="gs-text-muted-light">请选择一个游戏，然后点击右侧按钮提交申请</p>
                        )}
                      </div>

                      {selectedGame?.existingApplicationId ? (
                        <a
                          href={`/search/track/${selectedGame.existingApplicationId}`}
                          className="gs-btn gs-btn-outline gs-btn-action"
                        >
                          查看现有申请 #{selectedGame.existingApplicationId}
                        </a>
                      ) : selectedGame?.isInLibrary ? (
                        <a
                          href={`/game/${selectedGame.libraryGameId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gs-btn gs-btn-gold gs-btn-action"
                        >
                          现在游玩
                        </a>
                      ) : selectedGame?.isEmulatable === 'no' ? (
                        <p className="gs-unavailable-message">此游戏无法在网页运行</p>
                      ) : (
                        <button
                          onClick={() => handleSubmit()}
                          disabled={!selectedGame || isSubmitting}
                          className={`gs-btn gs-btn-action ${
                            !selectedGame || isSubmitting
                              ? 'gs-btn-disabled'
                              : selectedGame.isEmulatable === 'yes'
                                ? 'gs-btn-gold'
                                : 'gs-btn-yellow'
                          }`}
                        >
                          {isSubmitting ? '提交中...' : selectedGame
                            ? t.submit
                            : t.submit}
                        </button>
                      )}
                    {submitError && (
                      <p className="gs-submit-error">{submitError}</p>
                    )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {step === 'success' && (
              <div className="gs-fade-in gs-success-section">
                <div className="gs-success-icon">
                  <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="gs-success-title">{t.success}</h2>
                <p className="gs-success-message">{t.successMessage}</p>
                
                {selectedGame && (
                  <div className="gs-success-summary">
                    <h3>已申请游戏</h3>
                    <div className="gs-success-details">
                      <p><span className="gs-text-muted">{t.chineseName}:</span> {selectedGame.title_cn}</p>
                      <p><span className="gs-text-muted">{t.englishName}:</span> {selectedGame.title_en}</p>
                      <p><span className="gs-text-muted">{t.platform}:</span> {selectedGame.platform}</p>
                      <p><span className="gs-text-muted">{t.releaseYear}:</span> {selectedGame.release_date}</p>
                    </div>
                  </div>
                )}

                {submittedId && (
                  <div className="gs-tracking-info">
                    <p>您的申请编号为 <span className="gs-text-gold gs-text-bold">#{submittedId}</span></p>
                    <p className="gs-text-muted-sm">保存以下链接可随时查看申请进度：</p>
                    <div className="gs-tracking-url">
                      <span className="gs-tracking-url-text">{`${window.location.origin}/search/track/${submittedId}`}</span>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/search/track/${submittedId}`;
                          navigator.clipboard.writeText(url).then(() => {
                            const btn = document.getElementById('gs-copy-btn');
                            if (btn) { btn.textContent = '已复制'; setTimeout(() => { btn.textContent = '复制'; }, 2000); }
                          });
                        }}
                        id="gs-copy-btn"
                        className="gs-btn gs-btn-gold gs-btn-sm gs-btn-copy"
                      >
                        复制
                      </button>
                    </div>
                    <button
                      onClick={() => handleTrack(submittedId)}
                      className="gs-track-link"
                    >
                      立即查看进度 →
                    </button>
                  </div>
                )}
                
                <div className="gs-success-actions">
                  <button
                    onClick={handleReset}
                    className="gs-btn gs-btn-gold"
                  >
                    {t.backToHome}
                  </button>
                  <button
                    onClick={handleViewRecent}
                    className="gs-recent-link"
                  >
                    查看近期游戏申请
                  </button>
                </div>
              </div>
            )}

            {step === 'recent' && (
              <div className="gs-fade-in">
                <button 
                  onClick={handleReset}
                  className="gs-back-btn"
                >
                  ← {t.back}
                </button>
                
                <div className="gs-recent-heading-row">
                  <h2 className="gs-results-heading">近期游戏申请</h2>
                  {!recentLoading && recentApps.length > 0 && (
                    <span className="gs-recent-count">共 {recentApps.length} 条</span>
                  )}
                </div>
                
                {recentLoading ? (
                  <div className="gs-loading-center">
                    <div className="gs-spinner"></div>
                    <span>加载中...</span>
                  </div>
                ) : recentApps.length === 0 ? (
                  <div className="gs-no-results">
                    <p>暂无游戏申请</p>
                  </div>
                ) : (
                  <div className="gs-results-list">
                    {paginatedRecentApps.map((app) => {
                      const game = appToCandidate(app);
                      const status = STATUS_MAP[app.status] || STATUS_MAP.pending;
                      return (
                        <div key={app.id} className="gs-recent-item" onClick={() => handleTrack(app.id)}>
                          <GameCandidateCard
                            game={game}
                            isSelected={false}
                            onSelect={() => handleTrack(app.id)}
                            compact
                          />
                          <div className={`gs-status-bar ${status.bg}`}>
                            <div className="gs-status-bar-row">
                              <div className="gs-status-bar-left">
                                <span className="gs-text-muted-sm">当前状态</span>
                                <span className={`gs-status-label ${status.color}`}>{status.label}</span>
                              </div>
                              <span className="gs-text-muted-xs">
                                {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString('zh-CN') + ' 提交' : ''}
                              </span>
                            </div>

                            {app.status === 'added' && app.added_url && (
                              <a
                                href={app.added_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="gs-btn gs-btn-gold gs-btn-full gs-btn-sm"
                              >
                                游戏已添加，点击前往游玩
                              </a>
                            )}

                            {(app.admin_notes || app.reviewed_at) && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                                {app.admin_notes ? (
                                  <p className="gs-admin-note" style={{ margin: 0 }}>
                                    <span className="gs-text-muted-xs">备注：</span>{app.admin_notes}
                                  </p>
                                ) : <span />}
                                {app.reviewed_at && (
                                  <p className="gs-text-muted-xs" style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    审核时间：{new Date(app.reviewed_at).toLocaleString('zh-CN')}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {recentTotalPages > 1 && (
                      <nav className="gs-pagination" aria-label="近期申请分页">
                        <button
                          type="button"
                          className="gs-btn gs-btn-outline gs-btn-sm"
                          onClick={() => setRecentPage(page => Math.max(1, page - 1))}
                          disabled={recentPage === 1}
                        >
                          上一页
                        </button>
                        <span className="gs-pagination-label">第 {recentPage} / {recentTotalPages} 页</span>
                        <button
                          type="button"
                          className="gs-btn gs-btn-outline gs-btn-sm"
                          onClick={() => setRecentPage(page => Math.min(recentTotalPages, page + 1))}
                          disabled={recentPage === recentTotalPages}
                        >
                          下一页
                        </button>
                      </nav>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 'tracking' && (
              <div className="gs-fade-in">
                <button 
                  onClick={() => {
                    if (submittedId) { setStep('success'); }
                    else { handleViewRecent(); }
                  }}
                  className="gs-back-btn"
                >
                  ← 返回
                </button>
                
                {trackingLoading ? (
                  <div className="gs-loading-center">
                    <div className="gs-spinner"></div>
                    <span>查询中...</span>
                  </div>
                ) : trackingError ? (
                  <div className="gs-no-results gs-error-panel">
                    <p>{trackingError}</p>
                  </div>
                ) : trackingApp ? (() => {
                  const status = STATUS_MAP[trackingApp.status] || STATUS_MAP.pending;
                  return (
                  <div className="gs-tracking-detail">
                    <h2 className="gs-results-heading">
                      申请 #{trackingApp.id}
                    </h2>

                    <GameCandidateCard
                      game={appToCandidate(trackingApp)}
                      isSelected={false}
                      onSelect={() => {}}
                      compact
                    />

                    <div className={`gs-status-bar gs-status-bar-connected ${status.bg}`}>
                      <div className="gs-status-bar-row">
                        <div className="gs-status-bar-left">
                          <span className="gs-text-muted-sm">当前状态</span>
                          <span className={`gs-status-label ${status.color}`}>{status.label}</span>
                        </div>
                        <span className="gs-text-muted-xs">
                          {trackingApp.submitted_at ? new Date(trackingApp.submitted_at).toLocaleDateString('zh-CN') + ' 提交' : ''}
                        </span>
                      </div>

                      {trackingApp.status === 'added' && trackingApp.added_url && (
                        <a
                          href={trackingApp.added_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gs-btn gs-btn-gold gs-btn-full gs-btn-sm"
                        >
                          游戏已添加，点击前往游玩
                        </a>
                      )}

                      {(trackingApp.admin_notes || trackingApp.reviewed_at) && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                          {trackingApp.admin_notes ? (
                            <p className="gs-admin-note" style={{ margin: 0 }}>
                              <span className="gs-text-muted-xs">备注：</span>{trackingApp.admin_notes}
                            </p>
                          ) : <span />}
                          {trackingApp.reviewed_at && (
                            <p className="gs-text-muted-xs" style={{ margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                              审核时间：{new Date(trackingApp.reviewed_at).toLocaleString('zh-CN')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="gs-tracking-footer">
                      <button
                        onClick={handleViewRecent}
                        className="gs-recent-link"
                      >
                        查看所有近期申请
                      </button>
                    </div>
                  </div>
                  );
                })() : null}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
